import React from 'react';
import { MapPin, Plus, Trash2, Eye, EyeOff, Edit2 } from 'lucide-react';
import { Geofence } from '../lib/supabase';

interface GeofenceManagerProps {
  geofences: Geofence[];
  onAddGeofence: () => void;
  onToggleGeofence: (id: string, active: boolean) => void;
  onDeleteGeofence: (id: string) => void;
  onEditGeofence: (id: string) => void;
  selectedGeofence: string | null;
  onSelectGeofence: (id: string | null) => void;
}

const GeofenceManager: React.FC<GeofenceManagerProps> = ({
  geofences,
  onAddGeofence,
  onToggleGeofence,
  onDeleteGeofence,
  onEditGeofence,
  selectedGeofence,
  onSelectGeofence,
}) => {
  return (
    <div className="sidebar-section">
      <div className="flex items-center justify-between mb-4">
        <label className="sidebar-label">
          Geofences
        </label>
        <button
          onClick={onAddGeofence}
          className="p-2 bg-traqpack-yellow text-black rounded-lg hover:bg-traqpack-yellow/90 transition-all"
          title="Add new geofence"
        >
          <Plus className="w-4 h-4" />
        </button>
      </div>

      {geofences.length === 0 ? (
        <div className="text-center py-8">
          <MapPin className="w-8 h-8 text-black/20 mx-auto mb-2" />
          <p className="text-sm text-black/60">No geofences created</p>
          <button
            onClick={onAddGeofence}
            className="mt-3 text-sm text-traqpack-yellow hover:text-traqpack-yellow/80 font-medium"
          >
            Create your first geofence
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          {geofences.map((geofence) => (
            <div
              key={geofence.id}
              className={`border-2 rounded-xl transition-all ${
                selectedGeofence === geofence.id
                  ? 'border-traqpack-yellow bg-traqpack-yellow/10'
                  : 'border-black/20 bg-white'
              }`}
            >
              <div className="p-3">
                <div className="flex items-center justify-between">
                  <button
                    onClick={() => onSelectGeofence(selectedGeofence === geofence.id ? null : geofence.id)}
                    className="flex-1 text-left"
                  >
                    <div className="flex items-center space-x-2">
                      <div className={`w-3 h-3 rounded-full ${
                        geofence.active ? 'bg-green-500' : 'bg-gray-400'
                      }`} />
                      <span className="font-medium text-sm">{geofence.name}</span>
                    </div>
                  </button>
                  <div className="flex items-center space-x-1">
                    <button
                      onClick={() => onToggleGeofence(geofence.id, !geofence.active)}
                      className="p-1 hover:bg-black/10 rounded transition-colors"
                      title={geofence.active ? 'Deactivate' : 'Activate'}
                    >
                      {geofence.active ? (
                        <Eye className="w-4 h-4 text-green-600" />
                      ) : (
                        <EyeOff className="w-4 h-4 text-gray-500" />
                      )}
                    </button>
                    <button
                      onClick={() => onEditGeofence(geofence.id)}
                      className="p-1 hover:bg-black/10 rounded transition-colors"
                      title="Edit geofence"
                    >
                      <Edit2 className="w-4 h-4 text-black/60" />
                    </button>
                    <button
                      onClick={() => onDeleteGeofence(geofence.id)}
                      className="p-1 hover:bg-black/10 rounded transition-colors"
                      title="Delete geofence"
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </button>
                  </div>
                </div>

                {selectedGeofence === geofence.id && (
                  <div className="mt-3 pt-3 border-t border-black/10 text-xs text-black/60">
                    <div>Radius: {geofence.radius}m</div>
                    <div>Center: {geofence.latitude.toFixed(6)}, {geofence.longitude.toFixed(6)}</div>
                    <div>Devices tracked: {geofence.devices?.length || 'All'}</div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default GeofenceManager;